import {
  Box,
  Divider,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { AppCheckbox } from 'components/atoms/StyledFormControls';
import { TranslatedTextV2 } from 'components/atoms/TranslatedText';
import UncontrolledSelectField from 'components/atoms/UncontrolledSelectField';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isObjectNullish } from 'utils/objects';
import {
  ITabContentProps,
  PropertyFilterType,
  PublishedOptions,
  UpdatedFromOptions,
} from '../configuration';
import ProductFilterActions from '../store/actions';
import * as ProductFilterSelectors from '../store/selectors';
import { ISpecOption } from 'services/api/product';
import { useSelectLanguageID } from 'redux/reducers/gl_translations_reducer/selectors';
import CircularProgress from '@material-ui/core/CircularProgress';
import { StateStatus } from 'redux/utils/common';
import {
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import SelectField from './SelectField';
import useStyles from '../styles';

const TabAdminFilters = (props: ITabContentProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const dataAdminFilters =
    props.selections[PropertyFilterType.AdminFilters].data;
  const propertyStatuses = ProductFilterSelectors.useGetProductStatuses();
  const segments = ProductFilterSelectors.useGetSegments();
  const language = useSelectLanguageID();
  const agents = ProductFilterSelectors.useGetAgents();
  const customers = ProductFilterSelectors.useGetClientCustomers();
  React.useEffect(() => {
    if (agents.length === 0) {
      dispatch(ProductFilterActions.fetchAgentsAction());
    }
    if (!segments.data) {
      dispatch(
        ProductFilterActions.fetchSegmentsAction({ language: language })
      );
    }
    if (customers?.length === 0) {
      dispatch(ProductFilterActions.fetchClientCustomersAction());
    }
  }, [dispatch]);

  const onValueChange = (
    key: string,
    value: string | number | (string | number)[] | null | Date
  ) => {
    if (value === 'null') value = null;
    dispatch(
      ProductFilterActions.setFiltersSelectionAction({
        ...props.selections,
        [PropertyFilterType.AdminFilters]: {
          ...props.selections[PropertyFilterType.AdminFilters],
          isChecked: !isObjectNullish({
            ...dataAdminFilters,
            [key]: value,
          }),
          data: {
            ...dataAdminFilters,
            [key]: value,
          },
        },
      })
    );
  };
  return (
    <Box
      style={{
        position: 'relative',
      }}
    >
      <Typography
        style={{
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '20px',
          marginBottom: '4px',
        }}
      >
        <TranslatedTextV2 translationKey={''} defaultText={'Admin filters'} />
      </Typography>
      <Typography
        style={{
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          color: theme.palette.text.secondary,
          marginBottom: '18px',
        }}
      >
        <TranslatedTextV2
          translationKey={''}
          defaultText={'Select one or more'}
        />
      </Typography>
      <Box>
        <UncontrolledSelectField
          label={{
            translationKey: 'visibility_status',
            defaultText: 'Visibility status',
          }}
          options={PublishedOptions}
          onValueChange={(value) => onValueChange('published', value)}
          field={'published'}
          value={dataAdminFilters.published ?? ''}
          menuPortalTarget={null}
          closeMenuOnSelect={true}
        />
        <Box marginBottom={'12px'}></Box>
        <UncontrolledSelectField
          label={{
            translationKey: 'availability_status',
            defaultText: 'Availability Status',
          }}
          options={
            propertyStatuses &&
            propertyStatuses.availability_status &&
            propertyStatuses.availability_status.options
              ? (propertyStatuses.availability_status.options.map(
                  (option: ISpecOption, index: number) => {
                    return {
                      id: option.id,
                      label: option.name,
                    };
                  }
                ) as any)
              : []
          }
          onValueChange={(value) => onValueChange('availability_status', value)}
          field={'availability_status'}
          value={dataAdminFilters.availability_status ?? []}
          menuPortalTarget={null}
          isMulti
        />
        <Box marginBottom={'12px'}></Box>
        <UncontrolledSelectField
          label={{
            translationKey: '',
            defaultText: 'Managing Agent',
          }}
          options={
            agents?.length > 0
              ? (agents.map((option: ISpecOption, index: number) => {
                  return {
                    id: option.id,
                    label: option.name,
                  };
                }) as any)
              : []
          }
          onValueChange={(value) => onValueChange('agents', value)}
          field={'agents'}
          value={dataAdminFilters.agents ?? []}
          menuPortalTarget={null}
          isMulti
        />
        <Box marginBottom={'12px'}></Box>
        <UncontrolledSelectField
          label={{
            translationKey: '',
            defaultText: 'Owner',
          }}
          options={
            customers?.length > 0
              ? (customers.map((option: ISpecOption, index: number) => {
                  return {
                    id: option.id,
                    label: option.name,
                  };
                }) as any)
              : []
          }
          onValueChange={(value) => onValueChange('owners', value)}
          field={'owners'}
          value={dataAdminFilters.owners ?? []}
          menuPortalTarget={null}
          isMulti
        />
        <Box marginBottom={'12px'}></Box>
        <UncontrolledSelectField
          label={{
            translationKey: '',
            defaultText: 'Refered by',
          }}
          options={
            customers?.length > 0
              ? (customers.map((option: ISpecOption, index: number) => {
                  return {
                    id: option.id,
                    label: option.name,
                  };
                }) as any)
              : []
          }
          onValueChange={(value) => onValueChange('refered_by', value)}
          field={'refered_by'}
          value={dataAdminFilters.refered_by ?? []}
          menuPortalTarget={null}
          isMulti
        />
        <Box marginBottom={'12px'}></Box>
        <UncontrolledSelectField
          label={{
            translationKey: '',
            defaultText: 'Representative',
          }}
          options={
            customers?.length > 0
              ? (customers.map((option: ISpecOption, index: number) => {
                  return {
                    id: option.id,
                    label: option.name,
                  };
                }) as any)
              : []
          }
          onValueChange={(value) => onValueChange('representative', value)}
          field={'representative'}
          value={dataAdminFilters.representative ?? []}
          menuPortalTarget={null}
          isMulti
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2 translationKey={''} defaultText={'Date added'} />
        </Typography>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDateTimePicker
            fullWidth
            disableToolbar
            onChange={(date, value) => {
              onValueChange('dateAdded', date);
            }}
            style={{ marginTop: '.75rem' }}
            format={'dd-MM-yyyy'}
            inputVariant={'outlined'}
            value={dataAdminFilters.dateAdded}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            placeholder={'Choose a date'}
            size="small"
            views={['year', 'month', 'date']}
          />
        </MuiPickersUtilsProvider>
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <SelectField
          label={{ translationKey: '', defaultText: 'Have not been updated' }}
          options={UpdatedFromOptions}
          onValueChange={onValueChange}
          field={'updated_from'}
          value={dataAdminFilters.updated_from}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2
            translationKey={''}
            defaultText={'Registration department'}
          />
        </Typography>
        <TextField
          onChange={(e) =>
            onValueChange('registrationdepartment', e.target.value)
          }
          value={dataAdminFilters.registrationdepartment || ''}
          variant="outlined"
          className={classes.boxInput}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2
            translationKey={''}
            defaultText={'Registration number'}
          />
        </Typography>
        <TextField
          onChange={(e) => onValueChange('registrationnumber', e.target.value)}
          value={dataAdminFilters.registrationnumber || ''}
          variant="outlined"
          className={classes.boxInput}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2 translationKey={''} defaultText={'Sheet'} />
        </Typography>
        <TextField
          onChange={(e) => onValueChange('sheet', e.target.value)}
          value={dataAdminFilters.sheet || ''}
          variant="outlined"
          className={classes.boxInput}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2 translationKey={''} defaultText={'Plan'} />
        </Typography>
        <TextField
          onChange={(e) => onValueChange('plan', e.target.value)}
          value={dataAdminFilters.plan || ''}
          variant="outlined"
          className={classes.boxInput}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2 translationKey={''} defaultText={'Block'} />
        </Typography>
        <TextField
          onChange={(e) => onValueChange('block', e.target.value)}
          value={dataAdminFilters.block || ''}
          variant="outlined"
          className={classes.boxInput}
        />
        <Box marginBottom={'12px'}></Box>
      </Box>
      <Box>
        <Typography style={{ fontWeight: 500, fontSize: '14px' }}>
          <TranslatedTextV2 translationKey={''} defaultText={'Parcel number'} />
        </Typography>
        <TextField
          onChange={(e) => onValueChange('parcelnumber', e.target.value)}
          value={dataAdminFilters.parcelnumber || ''}
          variant="outlined"
          className={classes.boxInput}
          type="number"
          inputMode="decimal"
        />
        <Box style={{ height: 20 }}></Box>
      </Box>
      {segments.status === StateStatus.Pending ? (
        <Box
          style={{
            display: 'flex',
            position: 'absolute',
            right: '50%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
    </Box>
  );
};

export default TabAdminFilters;
